import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { 
  Bell, X, CheckCheck, Crown, Wallet, Sparkles, ShieldCheck, Info, ArrowRight 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const getNotificationDetails = (notification) => {
  const text = `${notification.title || ''} ${notification.message || ''} ${notification.type || ''}`.toLowerCase();
  
  if (text.includes('suscrip') || text.includes('premium') || text.includes('pro') || text.includes('plan')) {
    return {
      icon: Crown,
      bg: 'bg-amber-500/10 text-amber-600 border-amber-200/50',
      dotBg: 'bg-amber-500'
    };
  }
  if (text.includes('gasto') || text.includes('transac') || text.includes('cuenta') || text.includes('saldo')) {
    return {
      icon: Wallet,
      bg: 'bg-emerald-500/10 text-emerald-600 border-emerald-200/50',
      dotBg: 'bg-emerald-500'
    };
  }
  if (text.includes('ia') || text.includes('voice') || text.includes('burbuja')) {
    return {
      icon: Sparkles,
      bg: 'bg-purple-500/10 text-purple-600 border-purple-200/50',
      dotBg: 'bg-purple-500'
    };
  }
  if (text.includes('segurid') || text.includes('clave') || text.includes('admin')) {
    return {
      icon: ShieldCheck,
      bg: 'bg-blue-500/10 text-blue-600 border-blue-200/50',
      dotBg: 'bg-blue-500'
    };
  }
  return {
    icon: Bell,
    bg: 'bg-slate-500/10 text-slate-700 border-slate-200/50',
    dotBg: 'bg-blue-600'
  };
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Hace un momento';
  if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
  if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`;
  if (diffInSeconds < 172800) return 'Ayer';
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
};

const NotificationBell = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'unread'

  const [touchStart, setTouchStart] = useState(0);
  const [touchY, setTouchY] = useState(0);

  useEffect(() => {
    if (showPanel) {
      document.body.style.overflow = 'hidden';
      if (unreadCount > 0) {
        markAllAsRead();
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPanel]);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
    setTouchY(0);
  };

  const handleTouchMove = (e) => {
    const currentY = e.targetTouches[0].clientY;
    const diff = currentY - touchStart;
    if (diff > 0) {
      setTouchY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (touchY > 60) {
      setShowPanel(false);
    }
    setTouchY(0);
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.type === 'feedback_update' && notification.feedback_id) {
      setShowPanel(false);
      navigate('/feedback');
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (activeFilter === 'unread') return !n.read;
    return true;
  });

  return (
    <div className="relative">
      {/* Bell Icon Button */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="relative p-2.5 rounded-2xl text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 active:scale-95 transition-all"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-black text-white bg-red-500 rounded-full border-2 border-white shadow-sm animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel Container (rendered via Portal) */}
      {showPanel && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-sm lg:bg-transparent lg:backdrop-blur-none transition-opacity"
            onClick={() => setShowPanel(false)}
          />

          {/* Panel Layout (Mobile Bottom Sheet + Desktop Dropdown) */}
          <div 
            style={{ 
              transform: touchY > 0 ? `translateY(${touchY}px)` : 'none',
              transition: touchY === 0 ? 'transform 0.2s ease-out' : 'none' 
            }}
            className="fixed inset-x-0 bottom-0 lg:bottom-auto lg:top-16 lg:right-6 lg:left-auto z-50 bg-white rounded-t-[2.2rem] lg:rounded-3xl p-4 sm:p-5 shadow-2xl w-full lg:w-[420px] max-h-[82vh] lg:max-h-[34rem] flex flex-col border-t lg:border border-slate-200/80 animate-in slide-in-from-bottom duration-200"
          >
            
            {/* Header Handle / Drag Zone (Mobile) */}
            <div 
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className="py-1 cursor-grab active:cursor-grabbing lg:hidden shrink-0"
            >
              <div className="w-12 h-1.5 bg-slate-300 rounded-full mx-auto mb-2" />
            </div>

            {/* Header Title + Actions */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-black text-slate-900 tracking-tight">Notificaciones</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 font-extrabold text-xs rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    disabled={loading}
                    className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                    title="Marcar todas como leídas"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Leídas</span>
                  </button>
                )}
                {/* X solo visible en desktop */}
                <button
                  onClick={() => setShowPanel(false)}
                  className="hidden lg:flex p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Tabs Filter (Todas / No leídas) */}
            <div className="flex items-center gap-2 py-3 border-b border-slate-100/80 shrink-0">
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-3 py-1.5 rounded-xl font-bold text-xs transition-all ${
                  activeFilter === 'all'
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200/60'
                }`}
              >
                Todas ({notifications.length})
              </button>
              <button
                onClick={() => setActiveFilter('unread')}
                className={`px-3 py-1.5 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 ${
                  activeFilter === 'unread'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200/60'
                }`}
              >
                <span>No leídas</span>
                {unreadCount > 0 && (
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                    activeFilter === 'unread' ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>

            {/* List Body */}
            <div className="overflow-y-auto flex-1 divide-y divide-slate-100/70 pt-1 no-scrollbar">
              {filteredNotifications.length === 0 ? (
                <div className="py-12 text-center flex flex-col items-center justify-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-slate-100 to-slate-200/60 rounded-2xl flex items-center justify-center mb-3 shadow-inner">
                    <Bell className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-sm font-bold text-slate-800">
                    {activeFilter === 'unread' ? 'No tienes sin leer' : '¡Todo al día!'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1 max-w-[220px]">
                    {activeFilter === 'unread'
                      ? 'Has leído todas tus notificaciones.'
                      : 'Te avisaremos cuando haya movimientos o noticias importantes.'}
                  </p>
                </div>
              ) : (
                filteredNotifications.map((notification) => {
                  const details = getNotificationDetails(notification);
                  const IconComponent = details.icon;
                  const isUnread = !notification.read;

                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-3.5 flex items-start gap-3.5 rounded-2xl cursor-pointer transition-all ${
                        isUnread
                          ? 'bg-blue-50/40 hover:bg-blue-50/70 border-l-4 border-blue-600'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className={`p-2.5 rounded-2xl border shrink-0 flex items-center justify-center ${details.bg}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <p className={`text-xs sm:text-sm font-bold leading-snug truncate ${
                            isUnread ? 'text-slate-900 font-extrabold' : 'text-slate-700'
                          }`}>
                            {notification.title}
                          </p>
                          <span className="text-[10px] font-medium text-slate-400 shrink-0">
                            {formatRelativeTime(notification.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
                          {notification.message}
                        </p>
                      </div>
                      {isUnread && (
                        <span className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${details.dotBg}`} />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;

